import { Link } from "react-router-dom";
import { tours } from "@/data/tours";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Clock, MapPin, ArrowRight } from "lucide-react";

export const TourPackagesSection = () => {
  const featuredTours = tours.slice(0, 3);

  return (
    <section className="bg-surface-subtle section-padding" data-testid="tour-packages">
      <div className="container-wide px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="overline mb-3">Curated for You</p>
              <h2 className="heading-lg text-primary">Tour Packages</h2>
            </div>
            <Link
              to="/tours"
              className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-primary transition-colors"
              data-testid="tours-view-all"
            >
              View all tours <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {featuredTours.map((tour, i) => (
            <AnimatedSection key={tour.id} delay={i * 0.08}>
              <div
                className="group bg-white rounded-xl shadow-card hover:shadow-card-hover transition-shadow duration-200 overflow-hidden flex flex-col h-full"
                data-testid={`tour-card-${i + 1}`}
              >
                {/* Image */}
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={tour.image}
                    alt={tour.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.2,0,0,1)] group-hover:scale-105"
                    loading="lazy"
                  />
                  <Badge variant="accent" className="absolute top-3 left-3">
                    {tour.region}
                  </Badge>
                  <div className="absolute top-3 right-3 bg-white/95 rounded-lg px-2 py-1 flex items-center gap-1">
                    <Star className="h-3 w-3 fill-accent text-accent" />
                    <span className="text-xs font-bold text-primary">{tour.rating}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2.5">
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 shrink-0" /> {tour.destination}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 shrink-0" /> {tour.duration}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-primary mb-2 group-hover:text-accent transition-colors duration-[120ms] line-clamp-2">
                    {tour.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1 leading-relaxed">
                    {tour.description}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-border/60">
                    <div>
                      <p className="text-xs text-muted-foreground">From</p>
                      <p className="text-xl font-bold text-primary">${tour.price.toLocaleString()}</p>
                    </div>
                    <Link to={`/tours/${tour.id}`}>
                      <Button size="sm" variant="outline">
                        View Details <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <Link
          to="/tours"
          className="sm:hidden flex items-center justify-center gap-2 text-sm font-semibold text-accent mt-6 hover:text-primary transition-colors"
          data-testid="tours-view-all-mobile"
        >
          View All Tours <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
};
